import React, { useState } from 'react';
import { X, Upload, Download } from 'lucide-react';
import { message } from 'antd';

function BulkStatusUpdateModal({ isOpen, onClose, onSave }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel') {
        setUploadedFile(file);
        simulateUpload(file);
      } else {
        message.warning('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  const simulateUpload = (file) => {
    setUploading(true);
    // Simulate file upload progress
    setTimeout(() => {
      setUploading(false);
    }, 2000);
  };

  const handleDownloadTemplate = () => {
    // Create a sample Excel template
    const templateData = [
      ['Order ID', 'Status', 'Tracking Number', 'Courier', 'Remarks'],
      ['OD3455', 'Dispatched', 'TRACK12345', 'Shadowfax', 'Sample remark'],
      ['OD3456', 'In Transit', 'TRACK12346', 'DNST', ''],
    ];

    // Convert to CSV for simplicity (you can use a library like xlsx for proper Excel)
    const csv = templateData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_status_update_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSave = () => {
    if (!uploadedFile) {
      message.warning('Please upload a file first');
      return;
    }
    onSave(uploadedFile);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Bulk status update</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Step 1: Download Template */}
          <div>
            <div className="flex items-start space-x-3 mb-3">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-800 mb-1">
                  Please download Excel file template.
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Choose and download any template and fill in your data.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4 text-teal-600" />
                  <span className="text-sm font-medium text-gray-700">Download</span>
                </button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload Template */}
          <div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-800 mb-1">
                  Please upload updated Excel file template.
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Please upload filled data here.
                </p>

                {/* Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                  {uploading ? (
                    <div className="text-center">
                      <div className="inline-block w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-sm text-gray-600">
                        November month status....
                      </p>
                      <div className="w-full max-w-xs mx-auto mt-3 bg-gray-200 rounded-full h-2">
                        <div className="bg-teal-500 h-2 rounded-full" style={{ width: '60%' }}></div>
                      </div>
                    </div>
                  ) : uploadedFile ? (
                    <div className="text-center">
                      <div className="inline-block p-3 bg-green-100 rounded-full mb-3">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-600">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </p>
                      <button
                        onClick={() => setUploadedFile(null)}
                        className="mt-3 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <label className="cursor-pointer">
                        <span className="text-teal-600 hover:text-teal-700 font-medium">
                          Click to Upload
                        </span>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Upload Excel file (.xlsx, .xls)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleSave}
            disabled={!uploadedFile || uploading}
            className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkStatusUpdateModal;